<template>
  <div id="contenedor-tablero-expedientes-tarjetas">
    <v-row class="mb-2">
      <v-col
        v-for="(kpi, index) in tarjetasKpi"
        :key="index"
        cols="12"
        sm="6"
        md="4"
        lg="3"
        class="d-flex">
        <v-card
          :class="['card-kpi', kpi.clase]"
          elevation="2"
          hover
          style="width: 100%">
          <v-card-text class="pa-5 d-flex flex-column" style="height: 100%">
            <div class="d-flex align-start mb-3">
              <v-avatar :color="kpi.color" size="48" class="mr-3 flex-shrink-0">
                <v-icon color="white">{{ kpi.icono }}</v-icon>
              </v-avatar>
              <div class="flex-grow-1">
                <div class="d-flex align-center mb-1">
                  <span class="text-caption text--secondary text-uppercase"
                    style="font-weight: 500; letter-spacing: 0.5px">
                    {{ kpi.titulo }}
                  </span>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        small
                        class="ml-1"
                        color="grey lighten-1">
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <span>{{ kpi.tooltip }}</span>
                  </v-tooltip>
                </div>
                <div class="text-h6 font-weight-bold">
                  {{ kpi.valor }}
                </div>
              </div>
            </div>
            <div class="mt-auto">
              <v-chip
                v-if="kpi.etiqueta"
                :color="kpi.colorEtiqueta"
                small
                text-color="white"
                class="font-weight-bold">
                {{ kpi.etiqueta }}
              </v-chip>
              <div v-else style="height: 24px"></div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import calcularTotalesGlobales from '@/helpers/expedienteTotales';

export default {
  name: 'TableroExpedientesTarjetas',
  props: {
    resultados: {
      type: Array,
      required: true,
    },
  },
  computed: {
    totalesGlobales() {
      return calcularTotalesGlobales(this.resultados);
    },
    tarjetasKpi() {
      const sinExpediente = this.resultados.filter(
        ({ estadoExpediente }) => estadoExpediente === 'SIN_EXPEDIENTE',
      ).length;
      const sinDocumentosAplicables = this.resultados.filter(
        ({ estadoExpediente }) => estadoExpediente === 'SIN_DOCUMENTOS_APLICABLES',
      ).length;
      const { faltantes, integrados, porcentajeGlobal } = this.totalesGlobales;

      return [
        {
          titulo: 'Total de Contratos',
          valor: this.resultados.length,
          icono: 'mdi-file-document-multiple-outline',
          color: 'blue darken-1',
          clase: 'card-total',
          tooltip: 'Cantidad de contratos dentro del rango y filtros seleccionados.',
        },
        {
          titulo: 'Sin Expediente',
          valor: sinExpediente,
          icono: 'mdi-file-question-outline',
          color: 'grey darken-1',
          clase: 'card-sin-expediente',
          etiqueta: sinExpediente > 0 ? 'Revisar' : null,
          colorEtiqueta: 'grey darken-1',
          tooltip: 'Contratos que no tienen ningún registro documental en su expediente.',
        },
        {
          titulo: 'Sin Documentos Aplicables',
          valor: sinDocumentosAplicables,
          icono: 'mdi-file-remove-outline',
          color: 'grey darken-2',
          clase: 'card-sin-aplicables',
          etiqueta: sinDocumentosAplicables > 0 ? 'Revisar' : null,
          colorEtiqueta: 'grey darken-2',
          tooltip: 'Contratos con documentos en su expediente, pero ninguno marcado como aplicable.',
        },
        {
          titulo: 'Expedientes Completos',
          valor: this.resultados.filter(
            ({ estadoExpediente }) => estadoExpediente === 'COMPLETO',
          ).length,
          icono: 'mdi-file-check-outline',
          color: 'green darken-1',
          clase: 'card-completos',
          tooltip: 'Contratos con el 100% de sus documentos aplicables integrados.',
        },
        {
          titulo: 'Expedientes en Proceso',
          valor: this.resultados.filter(
            ({ estadoExpediente }) => !['COMPLETO', 'SIN_EXPEDIENTE', 'SIN_DOCUMENTOS_APLICABLES']
              .includes(estadoExpediente),
          ).length,
          icono: 'mdi-file-clock-outline',
          color: 'orange darken-1',
          clase: 'card-proceso',
          tooltip: 'Contratos con expediente iniciado pero aún sin integrar al 100%.',
        },
        {
          titulo: 'Documentos Integrados',
          valor: integrados,
          icono: 'mdi-file-check',
          color: 'cyan darken-1',
          clase: 'card-integrados',
          tooltip: 'Suma de documentos aplicables que ya cuentan con archivo cargado.',
        },
        {
          titulo: 'Documentos Faltantes',
          valor: faltantes,
          icono: 'mdi-file-alert',
          color: 'red darken-1',
          clase: 'card-faltantes',
          etiqueta: faltantes > 0 ? 'Pendientes' : null,
          colorEtiqueta: 'red darken-1',
          tooltip: 'Suma de documentos aplicables que aún no tienen archivo cargado.',
        },
        {
          titulo: 'Porcentaje Global',
          valor: `${porcentajeGlobal}%`,
          icono: 'mdi-percent-outline',
          color: 'purple darken-1',
          clase: 'card-global',
          tooltip: 'Documentos integrados entre documentos aplicables de todo el conjunto (no es el promedio de los porcentajes por contrato).',
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.card-kpi {
  border-radius: 12px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 4px solid transparent;
  background: white;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
  }

  &.card-total {
    border-left-color: #1976d2;
  }

  &.card-sin-expediente {
    border-left-color: #757575;
  }

  &.card-sin-aplicables {
    border-left-color: #424242;
  }

  &.card-completos {
    border-left-color: #388e3c;
  }

  &.card-proceso {
    border-left-color: #f57c00;
  }

  &.card-integrados {
    border-left-color: #0097a7;
  }

  &.card-faltantes {
    border-left-color: #d32f2f;
    background: linear-gradient(135deg, #ffffff 0%, #fef5f5 100%);
  }

  &.card-global {
    border-left-color: #7b1fa2;
  }

  .text-caption {
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .text-h6 {
    line-height: 1.2;
    color: #263238;
  }
}
</style>
